import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaSync, FaImage } from "react-icons/fa";
import { getAllApplications } from "../../../store/slices/certificates/asyncThunks";
import {
  selectAllApplications,
  selectAllApplicationsLoading,
} from "../../../store/slices/certificates/slice";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  DataTable,
  Filters,
  DataTableScroll,
  TableHeader,
  TableRow,
  TableCell,
  LoadingCell,
  EmptyCell,
  StatusBadge,
  UserInfo,
  CourseInfo,
} from "./AdminCertificates.styles";
import CustomSelect from "../../../components/utils/CustomSelect";

const AdminCertificates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const applications = useSelector(selectAllApplications);
  const loading = useSelector(selectAllApplicationsLoading);

  // Filters
  const [filters, setFilters] = useState({
    status: "",
  });

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line
  }, [filters, dispatch]);

  const loadApplications = () => {
    dispatch(getAllApplications(filters));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleViewApplication = (application) => {
    navigate(`/admin-panel/sertifikati/${application.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#fbbf24";
      case "approved":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Na čekanju";
      case "approved":
        return "Odobreno";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("sr-Latn-RS", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Aplikacije za Sertifikate</SectionTitle>
        <SectionActions>
          <Button
            $secondary
            onClick={loadApplications}
            disabled={loading}
            $loading={loading}
          >
            <FaSync />
            Osveži
          </Button>
        </SectionActions>
      </SectionHeader>

      <Filters>
        <CustomSelect
          $small
          value={filters.status}
          onChange={(status) => handleFilterChange("status", status)}
          options={[
            { value: "", label: "Svi statusi" },
            { value: "pending", label: "Na čekanju" },
            { value: "approved", label: "Odobreno" },
          ]}
          ariaLabel="Filtriraj po statusu aplikacije"
        />
      </Filters>

      <DataTableScroll>
        <DataTable>
          <TableHeader>Korisnik</TableHeader>
          <TableHeader>Kurs</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Datum</TableHeader>
          <TableHeader $align="right">Slike</TableHeader>
          {loading ? (
            <LoadingCell>
              <l-line-spinner
                size="24"
                stroke="3"
                speed="1"
                color="var(--accent)"
              />
            </LoadingCell>
          ) : applications.length === 0 ? (
            <EmptyCell>Nema aplikacija za sertifikate</EmptyCell>
          ) : (
            applications.map((application) => (
              <TableRow
                key={application.id}
                onClick={() => handleViewApplication(application)}
              >
                <TableCell>
                  <UserInfo>
                    <div>
                      {application.user.firstName &&
                      application.user.lastName ? (
                        <strong>
                          {application.user.firstName}{" "}
                          {application.user.lastName}
                        </strong>
                      ) : (
                        <strong>Nepoznat Korisnik</strong>
                      )}
                      <br />
                      <small>{application.user.email}</small>
                    </div>
                  </UserInfo>
                </TableCell>
                <TableCell style={{ whiteSpace: "wrap" }}>
                  <CourseInfo>{application.course.title}</CourseInfo>
                </TableCell>
                <TableCell>
                  <StatusBadge color={getStatusColor(application.status)}>
                    {getStatusText(application.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell>{formatDate(application.appliedAt)}</TableCell>
                <TableCell style={{ justifyContent: "flex-end" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <FaImage title="Slike rada korisnika" />
                    <span
                      style={{ fontSize: "12px", color: "var(--text-muted)" }}
                    >
                      {application.workImages
                        ? application.workImages.length
                        : 0}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </DataTable>
      </DataTableScroll>
    </>
  );
};

export default AdminCertificates;
